import React, {useEffect, useState} from "react";
import {createPortal} from "react-dom";
import {useMutation} from "@tanstack/react-query";
import {request} from "../../lib/request";
import {getErrorMessage, toastError, toastSuccess} from "../../lib/toast";

const TIME_STEP = 5;
const TIME_MIN = 5;
const TIME_MAX = 120;

const ModalTime = ({
    id,
    time,
    refetch,
    label,
    isPrint,
    hasNote,
    noteAcknowledged = true,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [modalArmed, setModalArmed] = useState(false);
    const [selectedTime, setSelectedTime] = useState("");
    const [note, setNote] = useState("");

    const blockedByNote = hasNote && !noteAcknowledged;

    useEffect(() => {
        if (!isOpen) {
            return undefined;
        }

        const onKeyDown = (event) => {
            if (event.key === "Escape") {
                setIsOpen(false);
            }
        };

        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        window.addEventListener("keydown", onKeyDown);

        return () => {
            document.body.style.overflow = previousOverflow;
            window.removeEventListener("keydown", onKeyDown);
        };
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen) {
            setModalArmed(false);
            return undefined;
        }

        setSelectedTime("");
        setNote("");
        setModalArmed(false);
        const armTimer = window.setTimeout(() => setModalArmed(true), 450);

        return () => window.clearTimeout(armTimer);
    }, [isOpen]);

    const {mutate, isPending} = useMutation({
        mutationFn: async (data) =>
            await request({
                url: "/restaurant/order/submit",
                method: "post",
                data,
            }),
        onSuccess: (data) => {
            toastSuccess(data.message || "سفارش با موفقیت تأیید شد");
            refetch();
            setIsOpen(false);
            if (typeof isPrint === "function") {
                setTimeout(() => isPrint(), 300);
            }
        },
        onError: (error) => {
            toastError(getErrorMessage(error, "خطا در تأیید سفارش"));
        },
    });

    const openModal = (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (blockedByNote) {
            toastError("لطفاً ابتدا توضیحات مشتری را تأیید کنید (تیک بزنید).");
            return;
        }
        setModalArmed(false);
        setIsOpen(true);
    };

    const closeModal = () => {
        if (!isPending) {
            setIsOpen(false);
        }
    };

    const adjustTime = (delta) => {
        setSelectedTime((current) => {
            const parsed = Number(current);
            // Empty field: first +/- should land on TIME_MIN (5), not TIME_MIN + step (10).
            if (!Number.isFinite(parsed) || parsed <= 0) {
                return String(TIME_MIN);
            }
            const next = Math.min(TIME_MAX, Math.max(TIME_MIN, parsed + delta));
            return String(next);
        });
    };

    const submitOrder = () => {
        if (!modalArmed || isPending) {
            return;
        }

        if (blockedByNote) {
            toastError("لطفاً ابتدا توضیحات مشتری را تأیید کنید (تیک بزنید).");
            return;
        }

        const readyMinutes = Number(selectedTime);
        if (time === "now" && (!Number.isFinite(readyMinutes) || readyMinutes <= 0)) {
            toastError("لطفاً زمان تقریبی را وارد کنید.");
            return;
        }

        mutate({
            time: time === "now" ? readyMinutes : null,
            admin_note: note.trim() || null,
            order_id: Number(id),
        });
    };

    const handleConfirm = (event) => {
        event.preventDefault();
        submitOrder();
    };

    return (
        <>
            <button
                type="button"
                onClick={openModal}
                disabled={isPending}
                className="rest-action-btn rest-action-btn--primary w-full"
            >
                {label}
            </button>

            {isOpen && createPortal(
                <div
                    className={`rest-accept-modal${modalArmed ? "" : " rest-accept-modal--arming"}`}
                    role="dialog"
                    aria-modal="true"
                    aria-label="تعیین زمان آماده‌سازی"
                    dir="rtl"
                >
                    <button
                        type="button"
                        className="rest-accept-modal__backdrop"
                        onClick={closeModal}
                        aria-label="بستن"
                        disabled={isPending}
                    />

                    <div className="rest-accept-modal__sheet">
                        <div className="rest-accept-modal__accent" aria-hidden="true"/>

                        <form onSubmit={handleConfirm} className="rest-accept-modal__form">
                            <h2 className="rest-accept-modal__title">تعیین زمان آماده‌سازی</h2>

                            <div className="rest-accept-modal__body">
                                {time === "now" ? (
                                    <div className="rest-accept-modal__field">
                                        <label
                                            className="rest-accept-modal__label"
                                            htmlFor={`accept-time-${id}`}
                                        >
                                            زمان تقریبی سفارش
                                        </label>
                                        <div className="rest-accept-modal__time-row">
                                            <button
                                                type="button"
                                                className="rest-accept-modal__step-btn"
                                                onClick={() => adjustTime(-TIME_STEP)}
                                                disabled={isPending || !modalArmed}
                                                aria-label="کاهش زمان"
                                            >
                                                −
                                            </button>
                                            <input
                                                id={`accept-time-${id}`}
                                                type="number"
                                                className="rest-accept-modal__input"
                                                min={TIME_MIN}
                                                max={TIME_MAX}
                                                step={TIME_STEP}
                                                value={selectedTime}
                                                onChange={(event) => setSelectedTime(event.target.value)}
                                                disabled={isPending || !modalArmed}
                                                inputMode="numeric"
                                                placeholder="—"
                                            />
                                            <button
                                                type="button"
                                                className="rest-accept-modal__step-btn"
                                                onClick={() => adjustTime(TIME_STEP)}
                                                disabled={isPending || !modalArmed}
                                                aria-label="افزایش زمان"
                                            >
                                                +
                                            </button>
                                            <span className="rest-accept-modal__suffix">دقیقه</span>
                                        </div>
                                    </div>
                                ) : (
                                    <p className="rest-accept-modal__scheduled">
                                        زمان تحویل سفارش
                                        <strong>{time}</strong>
                                    </p>
                                )}

                                <div className="rest-accept-modal__field">
                                    <label
                                        className="rest-accept-modal__label"
                                        htmlFor={`accept-note-${id}`}
                                    >
                                        توضیحات (اختیاری)
                                    </label>
                                    <textarea
                                        id={`accept-note-${id}`}
                                        className="rest-accept-modal__textarea"
                                        value={note}
                                        onChange={(event) => setNote(event.target.value)}
                                        disabled={isPending || !modalArmed}
                                        rows={4}
                                        placeholder="در صورت نیاز توضیحی برای غذارسان بنویسید..."
                                    />
                                </div>
                            </div>

                            <div className="rest-accept-modal__actions">
                                <button
                                    type="button"
                                    disabled={isPending || !modalArmed}
                                    onClick={submitOrder}
                                    className="rest-action-btn rest-action-btn--primary"
                                >
                                    {isPending ? "در حال تأیید..." : "تایید"}
                                </button>
                                <button
                                    type="button"
                                    disabled={isPending}
                                    onClick={closeModal}
                                    className="rest-action-btn rest-action-btn--ghost"
                                >
                                    انصراف
                                </button>
                            </div>
                        </form>
                    </div>
                </div>,
                document.body
            )}
        </>
    );
};

export default ModalTime;
